// Introducing section: a 2-column layout with a left and right block,
// each with a title + rich-text body. Optional in the default newsletter
// template — only renders if the user explicitly adds one.

import { useState } from "react"
import { Box, Button, Card, IconButton, Popover, TextField, Typography } from "@mui/material"
import TipTapEditor from "@crm-src/components/tiptap-editor"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { Trash2, Palette, Type } from "lucide-react"
// Sanitize untrusted rich-text HTML before injecting via dangerouslySetInnerHTML
// (DOMPurify under the hood). Same protection main applies in the visual preview.
import { sanitizeHtmlWithStyles } from "@crm-src/lib/sanitize"
import { NewsletterSection } from "./stock-images"

interface IntroducingSectionProps {
  section: NewsletterSection
  isEditing: boolean
  onUpdate: (sectionId: string, partial: any) => void
  onChangeColor: (sectionId: string, color: string) => void
  onDelete: (sectionId: string) => void
  setEditingSection: (id: string | null) => void
}

export function IntroducingSection({
  section,
  isEditing,
  onUpdate,
  onChangeColor,
  onDelete,
  setEditingSection,
}: IntroducingSectionProps) {
  const [colorAnchor, setColorAnchor] = useState<null | HTMLElement>(null)
  return (
    <Box
      className="group"
      sx={{ position: "relative" }}
      style={{ backgroundColor: section.backgroundColor, padding: '17px 40px', borderBottom: '3px solid #6B8E23' }}
    >
      {/* Section controls */}
      <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1, bgcolor: "rgba(255,255,255,0.9)", backdropFilter: "blur(4px)", borderRadius: 1, boxShadow: 1, p: 0.5 }}>
        <IconButton size="small" onClick={(e) => setColorAnchor(e.currentTarget)}>
          <Palette size={16} />
        </IconButton>
        <Popover
          open={Boolean(colorAnchor)}
          anchorEl={colorAnchor}
          onClose={() => setColorAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { width: 288, p: 2 } } }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography component="label" sx={{ fontSize: "1rem", fontWeight: 600 }}>Background Color</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
              {[
                { color: '#f9fdf7', label: 'Light Green' },
                { color: '#ffffff', label: 'White' },
                { color: '#e8f5e9', label: 'Soft Green' },
                { color: '#fff9e6', label: 'Cream' },
              ].map(({ color, label }) => (
                <Box
                  component="button"
                  key={color}
                  onClick={() => onChangeColor(section.id, color)}
                  sx={{ position: "relative", height: 64, borderRadius: 1, border: "2px solid", transition: "all 0.15s", cursor: "pointer", "&:hover": { transform: "scale(1.05)", boxShadow: 3 } }}
                  style={{ backgroundColor: color, borderColor: section.backgroundColor === color ? '#6B8E23' : '#d1d5db' }}
                >
                  <Box component="span" sx={{ position: "absolute", bottom: 4, left: 8, fontSize: 12, fontWeight: 500, color: "text.secondary" }}>{label}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Popover>
        <IconButton size="small" onClick={() => setEditingSection(section.id)}>
          <Type size={16} />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => onDelete(section.id)}>
          <Trash2 size={16} />
        </IconButton>
      </Box>

      {isEditing ? (
        <Card sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography component="label" sx={{ fontSize: "1rem", fontWeight: 600 }}>Left Column</Typography>
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Title</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <InlineAiGenerator
                      fieldType="short"
                      context="introduction section title - a catchy headline for your product/service introduction"
                      placeholder="e.g., innovative communication solutions"
                      currentValue={section.content.left.title}
                      onGenerate={(content) => onUpdate(section.id, {
                        left: { ...section.content.left, title: content }
                      })}
                    />
                    <AiTextImprover
                      fieldType="short"
                      context="introduction section title"
                      currentText={section.content.left.title}
                      onImprove={(content) => onUpdate(section.id, {
                        left: { ...section.content.left, title: content }
                      })}
                    />
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={section.content.left.title}
                  onChange={(e) => onUpdate(section.id, {
                    left: { ...section.content.left, title: e.target.value }
                  })}
                />
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Content</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <InlineAiGenerator
                      fieldType="long"
                      context="introduction content - describe your product/service features and benefits"
                      placeholder="e.g., explain what makes your solution unique"
                      currentValue={section.content.left.text}
                      onGenerate={(content) => onUpdate(section.id, {
                        left: { ...section.content.left, text: content }
                      })}
                    />
                    <AiTextImprover
                      fieldType="long"
                      context="introduction content"
                      currentText={section.content.left.text?.replace(/<[^>]*>/g, '') || ''}
                      onImprove={(content) => onUpdate(section.id, {
                        left: { ...section.content.left, text: `<p>${content}</p>` }
                      })}
                    />
                  </Box>
                </Box>
                <TipTapEditor
                  value={section.content.left.text}
                  onChange={(text) => onUpdate(section.id, {
                    left: { ...section.content.left, text }
                  })}
                  placeholder="Enter text content..."
                  minHeight={150}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography component="label" sx={{ fontSize: "1rem", fontWeight: 600 }}>Right Column</Typography>
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Title</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <InlineAiGenerator
                      fieldType="short"
                      context="introduction section title - a catchy headline for your product/service introduction"
                      placeholder="e.g., reliable support services"
                      currentValue={section.content.right.title}
                      onGenerate={(content) => onUpdate(section.id, {
                        right: { ...section.content.right, title: content }
                      })}
                    />
                    <AiTextImprover
                      fieldType="short"
                      context="introduction section title"
                      currentText={section.content.right.title}
                      onImprove={(content) => onUpdate(section.id, {
                        right: { ...section.content.right, title: content }
                      })}
                    />
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={section.content.right.title}
                  onChange={(e) => onUpdate(section.id, {
                    right: { ...section.content.right, title: e.target.value }
                  })}
                />
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Content</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <InlineAiGenerator
                      fieldType="long"
                      context="introduction content - describe your product/service features and benefits"
                      placeholder="e.g., explain your support offerings"
                      currentValue={section.content.right.text}
                      onGenerate={(content) => onUpdate(section.id, {
                        right: { ...section.content.right, text: content }
                      })}
                    />
                    <AiTextImprover
                      fieldType="long"
                      context="introduction content"
                      currentText={section.content.right.text?.replace(/<[^>]*>/g, '') || ''}
                      onImprove={(content) => onUpdate(section.id, {
                        right: { ...section.content.right, text: `<p>${content}</p>` }
                      })}
                    />
                  </Box>
                </Box>
                <TipTapEditor
                  value={section.content.right.text}
                  onChange={(text) => onUpdate(section.id, {
                    right: { ...section.content.right, text }
                  })}
                  placeholder="Enter text content..."
                  minHeight={150}
                />
            </Box>
          </Box>
        </Box>
          <Button size="small" color="success" variant="contained" onClick={() => setEditingSection(null)}>Save</Button>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
          <div>
            <h3 style={{ color: '#6B8E23', fontSize: '20px', marginBottom: '10px', fontWeight: 'bold', borderBottom: '2px solid #8FBC8F', paddingBottom: '8px', display: 'inline-block' }}>
              Introducing
            </h3>
            <h2 style={{ color: '#2d3748', fontSize: '22px', margin: '10px 0 15px 0', fontWeight: 'bold' }}>
              {section.content.left.title}
            </h2>
            <div
              style={{ color: '#4a5568', fontSize: '15px', lineHeight: '1.6', margin: 0 }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtmlWithStyles(section.content.left.text) }}
            />
          </div>
          <div>
            <h3 style={{ color: '#6B8E23', fontSize: '20px', marginBottom: '10px', fontWeight: 'bold', borderBottom: '2px solid #8FBC8F', paddingBottom: '8px', display: 'inline-block' }}>
              {section.content.right.title}
            </h3>
            <div
              style={{ color: '#4a5568', fontSize: '15px', lineHeight: '1.6', margin: '10px 0 0 0' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtmlWithStyles(section.content.right.text) }}
            />
          </div>
        </div>
      )}
    </Box>
  )
}

export function introducingToHtml(section: NewsletterSection): string {
  return `
          <!-- Introducing Section -->
          <tr>
            <td style="background-color: ${section.backgroundColor || '#f9fdf7'}; padding: 17px 40px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="width: 48%; vertical-align: top; padding-right: 4%;">
                    <h2 style="color: #6B8E23; font-size: 22px; margin: 0 0 10px 0; border-bottom: 3px solid #6B8E23; padding-bottom: 5px; display: inline-block; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;">
                      ${section.content.left.title}
                    </h2>
                    <div style="color: #2d3748; font-size: 14px; line-height: 1.6;">
                      ${section.content.left.text}
                    </div>
                  </td>
                  <td style="width: 48%; vertical-align: top;">
                    <h2 style="color: #6B8E23; font-size: 22px; margin: 0 0 10px 0; border-bottom: 3px solid #6B8E23; padding-bottom: 5px; display: inline-block; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;">
                      ${section.content.right.title}
                    </h2>
                    <div style="color: #2d3748; font-size: 14px; line-height: 1.6;">
                      ${section.content.right.text}
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
`
}
